import { createFileRoute, Link } from "@tanstack/react-router";
import { SECTION_OG, sectionImageMeta } from "@/lib/ogSections";
import { PageShell } from "@/components/PageShell";
import { ConsultingLinks } from "@/components/ConsultingExpertise";
import { Reveal } from "@/components/Reveal";
import { AuthorCard } from "@/components/AuthorCard";
import { SOCIAL } from "@/lib/site";
import { pageSchemaScript, PERSON_ID } from "@/seo/schema";
import { trackEvent } from "@/lib/analytics";
import heroImg from "@/assets/architects-hero.jpg";
import {
  ArrowRight,
  Mail,
  Home,
  ShieldCheck,
  Film,
  Mic2,
  Wifi,
  Flame,
  DoorOpen,
  Wrench,
  Building2,
  Trees,
  Presentation,
  Building,
  FileText,
  Compass,
  Layers,
  ClipboardCheck,
  HardHat,
  PackageCheck,
  Ruler,
  Linkedin,
} from "lucide-react";

const EMAIL = "arif@arifkhanglobal.com";
const MAILTO =
  `mailto:${EMAIL}?subject=${encodeURIComponent("Let's Talk — Architect Project Requirement")}` +
  `&body=${encodeURIComponent(
    [
      "Hello Arif,",
      "",
      "I would like to share a project for technology planning review.",
      "",
      "Project type:",
      "Project location:",
      "Current stage:",
      "Architect/interior scope:",
      "Technology areas required:",
      "Brief note:",
      "",
      "Regards,",
    ].join("\n"),
  )}`;

const CHECKLIST = [
  {
    Icon: Home,
    title: "Smart Home Automation",
    points: [
      "Lighting control",
      "Curtain automation",
      "HVAC integration",
      "Scene control",
      "Mobile / app control",
      "Future scalability",
    ],
  },
  {
    Icon: ShieldCheck,
    title: "Security Infrastructure",
    points: [
      "CCTV camera locations",
      "Access control doors",
      "Gate automation",
      "Video door phone",
      "Perimeter sensors",
      "Command / monitoring points",
    ],
  },
  {
    Icon: Film,
    title: "Home Cinema & Media Rooms",
    points: [
      "Room proportions",
      "Screen wall",
      "Speaker placement",
      "Acoustic treatment",
      "Seating layout",
      "Projector / LED planning",
      "HVAC noise control",
    ],
  },
  {
    Icon: Mic2,
    title: "Auditorium AV",
    points: [
      "Sound coverage",
      "Stage lighting",
      "Projection / LED display",
      "Control room location",
      "Acoustic coordination",
      "Hybrid event readiness",
    ],
  },
  {
    Icon: Wifi,
    title: "Network & Wi-Fi",
    points: [
      "Access point locations",
      "Rack location",
      "Cable pathways",
      "VLAN / security planning",
      "Future device capacity",
    ],
  },
  {
    Icon: Flame,
    title: "Fire & Life Safety Coordination",
    points: [
      "Detector locations",
      "Sounder / strobe placement",
      "Fire panel location",
      "Integration with access control",
      "Emergency release logic",
      "Maintenance access",
    ],
  },
  {
    Icon: DoorOpen,
    title: "Entrance & Visitor Experience",
    points: [
      "Gate automation",
      "Boom barrier",
      "Video door phone",
      "Visitor management",
      "Parking access",
      "First-impression experience",
    ],
  },
  {
    Icon: Wrench,
    title: "Serviceability",
    points: [
      "Rack ventilation",
      "Access panels",
      "Spare conduits",
      "Cable labelling",
      "Maintenance pathways",
      "Expansion planning",
    ],
  },
];

const MISTAKES = [
  { title: "Automation discussed after electrical wiring", impact: "Extra chasing, rework, switchboard confusion, limited scene control." },
  { title: "Home cinema planned after room dimensions are frozen", impact: "Poor acoustics, wrong seating distance, compromised speaker placement." },
  { title: "CCTV added after elevations are complete", impact: "Camera blind spots, ugly mounting points, exposed conduits." },
  { title: "Wi-Fi planned after interiors", impact: "Dead zones, weak coverage, visible access points, poor performance." },
  { title: "Rack location ignored", impact: "Heat, noise, service issues, cable mess, difficult maintenance." },
  { title: "Fire alarm coordination delayed", impact: "Detector / sounder conflicts, compliance pressure, late-stage changes." },
  { title: "Auditorium AV treated as a purchase", impact: "Sound issues, lighting mismatch, poor control, weak event experience." },
  { title: "Gate automation added at the end", impact: "Civil changes, power issues, control integration problems." },
];

const SUPPORT = [
  {
    Icon: Home,
    title: "Luxury Residences & Villas",
    points: ["Smart home automation", "Lighting and curtain control", "Security", "Gate automation", "Video door phone", "Home cinema", "Wi-Fi and network", "Multi-room audio"],
  },
  {
    Icon: Trees,
    title: "Farmhouses & Private Estates",
    points: ["Perimeter security", "Electric fence", "Long-range CCTV", "Gate automation", "Outdoor audio", "Landscape lighting coordination", "Remote monitoring"],
  },
  {
    Icon: Film,
    title: "Home Cinema & Media Rooms",
    points: ["Room planning", "Screen placement", "Speaker layout", "Acoustic coordination", "Seating layout", "Lighting scenes", "Control system"],
  },
  {
    Icon: Mic2,
    title: "Auditoriums & Institutions",
    points: ["Auditorium sound", "Stage lighting", "Projection / LED", "Acoustics coordination", "Control rooms", "Hybrid events", "Operator simplicity"],
  },
  {
    Icon: Presentation,
    title: "Corporate & Boardrooms",
    points: ["Boardroom AV", "Video conferencing", "Presentation systems", "Acoustic comfort", "Cable management", "User-friendly control"],
  },
  {
    Icon: Building2,
    title: "Intelligent Buildings",
    points: ["Security", "Automation", "AV", "Fire alarm coordination", "BMS / IBMS integration", "Scalable infrastructure"],
  },
];

const PROCESS = [
  { Icon: FileText, title: "Project Brief Review", body: "Understand the project type, design intent, user profile, and technology expectations." },
  { Icon: Compass, title: "Drawing Study", body: "Review plans, reflected ceiling plans, elevations, service areas, equipment rooms, and access points." },
  { Icon: Layers, title: "Technology Concept", body: "Prepare a practical system direction for security, automation, AV, network, and integration." },
  { Icon: Ruler, title: "Design Coordination", body: "Coordinate with architect, MEP / electrical consultant, PMC, civil team, and interior team." },
  { Icon: ClipboardCheck, title: "BOQ / Specification Support", body: "Help define system scope, equipment categories, infrastructure requirements, and execution expectations." },
  { Icon: HardHat, title: "Execution Review", body: "Support the team during site execution to reduce ambiguity, rework, and coordination gaps." },
  { Icon: PackageCheck, title: "Handover Readiness", body: "Ensure the final system is usable, maintainable, and aligned with the original design intent." },
];

const REVIEW_INPUTS = [
  "Floor plans", "Reflected ceiling plans", "Electrical drawings", "Elevations",
  "Room dimensions", "Door schedule", "Site photos", "BOQ, if available",
  "Client brief", "Project stage", "Technology expectations", "Budget range, if available",
];

const TIMELINE_STAGES = [
  { label: "Concept", highlight: true },
  { label: "Design Development", highlight: true },
  { label: "MEP Coordination", highlight: false },
  { label: "BOQ", highlight: false },
  { label: "Execution", highlight: false },
  { label: "Handover", highlight: false },
];

const AVOIDS = [
  "Ceiling conflicts", "Speaker placement mistakes", "Camera blind spots",
  "Weak Wi-Fi coverage", "Poor rack location", "Cable pathway issues",
  "Control confusion", "Late-stage BOQ changes", "Aesthetic compromise",
  "Maintenance access problems",
];

const LAYERS = [
  { name: "Security", body: "CCTV, access control, fire alarm, perimeter protection." },
  { name: "Automation", body: "Lighting, curtains, HVAC, scenes, sensors." },
  { name: "AV", body: "Speakers, displays, projection, microphones, control systems." },
  { name: "Network", body: "Wi-Fi, structured cabling, rack, switches, internet backbone." },
  { name: "Experience", body: "Comfort, convenience, safety, aesthetics, usability." },
];

const FAQS = [
  { q: "At what stage should technology planning begin?", a: "Ideally during concept or design development, before electrical layouts, ceiling design, wall finishes, and BOQ are frozen." },
  { q: "Does technology planning affect interior design?", a: "Yes. It affects switchboards, ceiling devices, speakers, sensors, access panels, rack location, cable pathways, and visible device placement." },
  { q: "Can smart home automation be planned after wiring?", a: "It can be done, but the options become limited and rework may increase. Early planning gives better aesthetics, control, and scalability." },
  { q: "Why is home cinema planning needed before interiors?", a: "Room size, screen wall, seating distance, speaker placement, acoustic treatment, HVAC noise, and lighting all affect the final experience." },
  { q: "What technology drawings are useful for architects?", a: "Device layout, conduit plan, rack location, cable pathway, speaker layout, camera coverage, Wi-Fi heat map, and integration notes are useful." },
  { q: "Can Arif coordinate with MEP consultants and PMCs?", a: "Yes. The advisory approach includes coordination with architects, electrical consultants, PMCs, contractors, and execution teams." },
  { q: "Is this only for luxury homes?", a: "No. The support is useful for luxury residences, farmhouses, auditoriums, institutions, corporate offices, industrial sites, and intelligent buildings." },
  { q: "Can this help reduce project rework?", a: "Yes. Early planning helps identify conflicts before site execution, reducing avoidable changes, exposed wiring, and delayed decisions." },
  { q: "Can architects share drawings confidentially?", a: "Yes. Project drawings and client details should be handled with confidentiality and used only for project review and planning discussion." },
  { q: "Does Arif recommend specific brands?", a: "The approach should remain requirement-led and vendor-neutral. OEM selection depends on design intent, integration needs, budget, serviceability, and long-term reliability." },
];

const TITLE = "Technology Planning Support for Architects | Arif Khan";
const DESCRIPTION =
  "Technology planning for architects and designers — security, automation, AV, home cinema, fire and access control, coordinated from concept to handover.";
const URL = "https://arifkhanglobal.com/for-architects";

export const Route = createFileRoute("/for-architects")({
  component: ForArchitectsPage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      {
        property: "og:description",
        content:
          "Early-stage technology planning for architects: smart home, security, AV, home cinema, auditorium, fire, access control, gate automation and intelligent buildings.",
      },
      { property: "og:url", content: "https://arifkhanglobal.com/for-architects" },
      { property: "og:type", content: "website" },
      ...sectionImageMeta(SECTION_OG.industries, "For architects — Arif Khan"),
    ],
    links: [
      { rel: "canonical", href: "https://arifkhanglobal.com/for-architects" },
      { rel: "alternate", type: "text/markdown", href: "https://arifkhanglobal.com/for-architects.md", title: "Markdown mirror" },
    ],
    scripts: [
      pageSchemaScript(
        {
          path: "/for-architects",
          type: "CollectionPage",
          name: TITLE,
          description: DESCRIPTION,
          breadcrumb: [{ name: "For Architects" }],
          extra: { about: { "@id": `${URL}#service` }, mainEntity: { "@id": `${URL}#service` } },
        },
        [
          {
            "@type": "Service",
            "@id": `${URL}#service`,
            name: "Technology Planning Support for Architects & Interior Designers",
            serviceType:
              "Technology consulting for architects: smart home automation, security infrastructure, AV, home cinema, auditorium, fire alarm, access control, gate automation, network and intelligent buildings.",
            description:
              "Early-stage technology planning for architects, interior designers, design studios, PMCs and consultants — coordinating security, automation, AV, fire, access, network and IBMS before drawings become expensive site changes.",
            provider: { "@id": PERSON_ID },
            areaServed: [
              "Delhi NCR", "New Delhi", "Noida", "Gurugram", "Faridabad", "Ghaziabad",
              "Mumbai", "Lucknow", "Bengaluru", "Hyderabad", "North India",
            ],
            audience: {
              "@type": "BusinessAudience",
              audienceType: "Architects, interior designers, design studios, PMCs and project consultants",
            },
          },
          {
            "@type": "FAQPage",
            "@id": `${URL}#faq`,
            url: URL,
            isPartOf: { "@id": `${URL}#webpage` },
            mainEntity: FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ],
      ),
    ],
  }),
});

function ForArchitectsPage() {
  return (
    <PageShell>
      {/* ============= HERO ============= */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-24 border-b border-border bg-surface overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none" />
        <div className="container-page relative">
          <div className="grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-16 items-center">
            <div>
              <span className="eyebrow">For Architects & Interior Designers</span>
              <h1 className="mt-5 text-balance text-4xl md:text-6xl font-semibold tracking-tight leading-[1.02]">
                Technology Planning Support for Architects & Interior Designers
              </h1>
              <p className="mt-6 max-w-xl text-pretty text-lg text-ink-soft leading-relaxed">
                Helping architects integrate security, automation, AV, home cinema, and intelligent
                building systems — before design decisions become costly site changes.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:opacity-90"
                >
                  Let's Talk <ArrowRight className="h-4 w-4" />
                </Link>
                <a
                  href={MAILTO}
                  className="inline-flex items-center gap-2 rounded-full border border-border-strong bg-background px-6 py-3 text-sm font-medium hover:border-primary hover:text-primary transition-colors"
                >
                  <Mail className="h-4 w-4" /> Email Your Requirement
                </a>
              </div>
              <p className="mt-8 text-xs uppercase tracking-[0.18em] text-ink-soft/80">
                23+ years across security infrastructure, smart automation, AV, home cinema,
                auditorium technology & intelligent buildings.
              </p>
            </div>

            <Reveal>
              <figure className="relative">
                <div className="rounded-2xl overflow-hidden border border-border bg-background shadow-[var(--shadow-card)]">
                  <img
                    src={heroImg}
                    alt="Architectural technology planning for luxury residence — section drawing with security, automation, AV and Wi-Fi layers"
                    width={1920}
                    height={1080}
                    fetchPriority="high"
                    decoding="async"
                    className="w-full h-auto block"
                  />
                </div>
              </figure>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============= SECTION 1: WHY EARLY ============= */}
      <section className="py-20 md:py-28 border-b border-border">
        <div className="container-page">
          <div className="max-w-3xl mb-12">
            <span className="eyebrow">Why early planning matters</span>
            <h2 className="mt-4 text-3xl md:text-4xl font-semibold tracking-tight">
              Why architects involve technology consultants early
            </h2>
            <p className="mt-5 text-base text-ink-soft leading-relaxed">
              Many technology problems begin when systems are discussed too late. By the time wiring,
              ceiling levels, wall finishes, and room dimensions are frozen, even small technology
              changes can create rework.
            </p>
          </div>

          {/* Timeline graphic */}
          <Reveal>
            <div className="rounded-2xl border border-border bg-surface p-6 md:p-10">
              <p className="text-xs uppercase tracking-[0.18em] text-ink-soft mb-6">
                Project timeline — ideal entry point
              </p>
              <ol className="grid grid-cols-2 md:grid-cols-6 gap-3 md:gap-0 md:divide-x md:divide-border">
                {TIMELINE_STAGES.map((s, i) => (
                  <li key={s.label} className="md:px-4 first:md:pl-0 last:md:pr-0">
                    <div className="flex items-center gap-3 md:flex-col md:items-start">
                      <span
                        className={`flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-medium tracking-tight ${
                          s.highlight
                            ? "bg-accent text-accent-foreground"
                            : "bg-background border border-border text-ink-soft"
                        }`}
                      >
                        {i + 1}
                      </span>
                      <div>
                        <p className={`text-sm font-medium ${s.highlight ? "text-foreground" : "text-ink-soft"}`}>
                          {s.label}
                        </p>
                        {s.highlight && (
                          <p className="text-[11px] uppercase tracking-[0.14em] text-accent mt-1">
                            Plan technology here
                          </p>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </Reveal>

          <div className="mt-10 grid md:grid-cols-2 gap-x-10 gap-y-3">
            <h3 className="text-sm font-semibold tracking-tight md:col-span-2">
              Early technology planning helps avoid
            </h3>
            {AVOIDS.map((a) => (
              <div key={a} className="flex items-start gap-3 text-sm text-ink-soft py-1.5 border-b border-border/60">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-accent flex-none" />
                <span>{a}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============= SECTION 2: CHECKLIST ============= */}
      <section className="py-20 md:py-28 border-b border-border bg-surface">
        <div className="container-page">
          <div className="max-w-3xl mb-12">
            <span className="eyebrow">For the design team</span>
            <h2 className="mt-4 text-3xl md:text-4xl font-semibold tracking-tight">
              The Architect's Technology Checklist
            </h2>
            <p className="mt-5 text-base text-ink-soft leading-relaxed">
              A clear view of what should be coordinated during design — not negotiated on site.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {CHECKLIST.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.03}>
                <div className="rounded-2xl border border-border bg-background p-6 h-full hover:border-border-strong transition-colors">
                  <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary-soft text-primary mb-4">
                    <c.Icon className="h-[18px] w-[18px]" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-base font-semibold tracking-tight">{c.title}</h3>
                  <ul className="mt-3 space-y-1.5">
                    {c.points.map((p) => (
                      <li key={p} className="text-[13px] text-ink-soft leading-relaxed flex gap-2">
                        <span className="mt-1.5 h-[3px] w-[3px] rounded-full bg-ink-soft/60 flex-none" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============= SECTION 3: LAYERS DIAGRAM ============= */}
      <section className="py-20 md:py-28 border-b border-border">
        <div className="container-page">
          <div className="max-w-3xl mb-12">
            <span className="eyebrow">A hidden layer of architecture</span>
            <h2 className="mt-4 text-3xl md:text-4xl font-semibold tracking-tight">
              Technology is a hidden layer of architecture
            </h2>
            <p className="mt-5 text-base text-ink-soft leading-relaxed">
              A well-designed building should not expose technology as clutter. The systems should be
              planned early, integrated quietly, and experienced effortlessly.
            </p>
          </div>

          <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
            <Reveal>
              <BuildingLayersDiagram />
            </Reveal>

            <ol className="space-y-3">
              {LAYERS.map((l, i) => (
                <li key={l.name} className="flex gap-4 rounded-xl border border-border bg-surface px-5 py-4">
                  <span className="text-xs font-mono text-ink-soft pt-1">0{i + 1}</span>
                  <div>
                    <h3 className="text-sm font-semibold tracking-tight">{l.name}</h3>
                    <p className="mt-1 text-[13px] text-ink-soft leading-relaxed">{l.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <div className="mt-12 flex flex-wrap gap-3">
            <a
              href={MAILTO}
              className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:opacity-90"
            >
              Plan Technology Before Execution <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* ============= SECTION 4: MISTAKES ============= */}
      <section className="py-20 md:py-28 border-b border-border bg-surface">
        <div className="container-page">
          <div className="max-w-3xl mb-12">
            <span className="eyebrow">Late change = expensive change</span>
            <h2 className="mt-4 text-3xl md:text-4xl font-semibold tracking-tight">
              Common design-stage mistakes that create site problems later
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {MISTAKES.map((m, i) => (
              <Reveal key={m.title} delay={i * 0.03}>
                <div className="rounded-2xl border border-border bg-background p-6 h-full">
                  <div className="flex items-start gap-4">
                    <span className="text-xs font-mono text-ink-soft pt-1">{String(i + 1).padStart(2, "0")}</span>
                    <div>
                      <h3 className="text-base font-semibold tracking-tight">{m.title}</h3>
                      <p className="mt-2 text-sm text-ink-soft leading-relaxed">
                        <span className="text-foreground/80 font-medium">Impact: </span>
                        {m.impact}
                      </p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============= SECTION 5: WHERE ARIF SUPPORTS ============= */}
      <section className="py-20 md:py-28 border-b border-border">
        <div className="container-page">
          <div className="max-w-3xl mb-12">
            <span className="eyebrow">Project types</span>
            <h2 className="mt-4 text-3xl md:text-4xl font-semibold tracking-tight">
              Where Arif supports architects
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SUPPORT.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.04}>
                <div className="rounded-2xl border border-border bg-background p-7 h-full hover:border-border-strong transition-colors">
                  <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary-soft text-primary mb-4">
                    <s.Icon className="h-[18px] w-[18px]" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-base font-semibold tracking-tight">{s.title}</h3>
                  <ul className="mt-3 space-y-1.5">
                    {s.points.map((p) => (
                      <li key={p} className="text-[13px] text-ink-soft leading-relaxed flex gap-2">
                        <span className="mt-1.5 h-[3px] w-[3px] rounded-full bg-ink-soft/60 flex-none" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============= SECTION 6: PROCESS ============= */}
      <section className="py-20 md:py-28 border-b border-border bg-surface">
        <div className="container-page">
          <div className="max-w-3xl mb-12">
            <span className="eyebrow">Collaboration</span>
            <h2 className="mt-4 text-3xl md:text-4xl font-semibold tracking-tight">
              A simple collaboration process
            </h2>
          </div>
          <ol className="relative grid md:grid-cols-2 gap-4">
            {PROCESS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.03}>
                <li className="flex gap-5 rounded-2xl border border-border bg-background p-6 h-full">
                  <div className="flex flex-col items-center">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-medium">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p.Icon className="h-4 w-4 text-primary" strokeWidth={1.6} />
                      <h3 className="text-base font-semibold tracking-tight">{p.title}</h3>
                    </div>
                    <p className="mt-2 text-sm text-ink-soft leading-relaxed">{p.body}</p>
                  </div>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* ============= SECTION 7: WHAT TO SHARE ============= */}
      <section className="py-20 md:py-28 border-b border-border">
        <div className="container-page">
          <div className="grid lg:grid-cols-[1fr_1.1fr] gap-12 lg:gap-16">
            <div>
              <span className="eyebrow">For a quick review</span>
              <h2 className="mt-4 text-3xl md:text-4xl font-semibold tracking-tight">
                What architects can share for a quick project review
              </h2>
              <p className="mt-5 text-base text-ink-soft leading-relaxed">
                Even a basic drawing review can reveal missing pathways, rack issues, ceiling
                conflicts, camera blind spots, and AV planning gaps before execution begins.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href={MAILTO}
                  className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:opacity-90"
                >
                  <Mail className="h-4 w-4" /> Email Your Requirement
                </a>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 rounded-full border border-border-strong bg-background px-6 py-3 text-sm font-medium hover:border-primary hover:text-primary transition-colors"
                >
                  Let's Talk
                </Link>
              </div>
              <p className="mt-6 text-xs text-ink-soft">
                Drawings are reviewed with confidentiality and used only for project planning discussion.
              </p>
            </div>

            <div className="rounded-2xl border border-border bg-surface p-6 md:p-8">
              <p className="text-xs uppercase tracking-[0.18em] text-ink-soft mb-5">Useful inputs</p>
              <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                {REVIEW_INPUTS.map((r) => (
                  <li key={r} className="flex items-start gap-3 text-sm">
                    <span className="mt-2 h-1.5 w-1.5 rounded-full bg-accent flex-none" />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ============= SECTION 8: DETAIL GRAPHICS ============= */}
      <section className="py-20 md:py-28 border-b border-border bg-surface">
        <div className="container-page">
          <div className="max-w-3xl mb-12">
            <span className="eyebrow">Architectural detail</span>
            <h2 className="mt-4 text-3xl md:text-4xl font-semibold tracking-tight">
              Planning illustrations
            </h2>
            <p className="mt-5 text-base text-ink-soft leading-relaxed">
              Elegant, drawing-style visuals that show how systems sit inside a building when planned
              in coordination with the architect.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <GraphicCard title="Hidden Infrastructure Map" caption="Cabling, rack, Wi-Fi, CCTV, AV and automation arranged as quiet layers behind the finished surface.">
              <InfrastructureMap />
            </GraphicCard>
            <GraphicCard title="Home Cinema Room Planning" caption="Screen wall, seating distance, speaker zones and acoustic treatment coordinated with room geometry.">
              <CinemaPlan />
            </GraphicCard>
            <GraphicCard title="Entrance Experience" caption="Gate automation, boom barrier, video door phone, access control and CCTV planned as one entrance journey.">
              <EntrancePlan />
            </GraphicCard>
            <GraphicCard title="Technology Planning Timeline" caption="Concept → Design → Coordination → BOQ → Execution → Handover.">
              <TimelineGraphic />
            </GraphicCard>
          </div>
        </div>
      </section>

      {/* ============= SECTION 9: FAQ ============= */}
      <section className="py-20 md:py-28 border-b border-border">
        <div className="container-page max-w-3xl">
          <span className="eyebrow">FAQ</span>
          <h2 className="mt-4 text-3xl md:text-4xl font-semibold tracking-tight">
            Questions architects often ask
          </h2>
          <div className="mt-10 divide-y divide-border border-t border-b border-border">
            {FAQS.map((f) => (
              <details key={f.q} className="group py-5">
                <summary className="cursor-pointer list-none flex items-start justify-between gap-6">
                  <span className="text-base font-semibold tracking-tight">{f.q}</span>
                  <span className="text-primary text-xl leading-none group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-sm text-ink-soft leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ============= SECTION J0: LINKEDIN MICRO-STORY ============= */}
      <section className="py-20 md:py-28 border-b border-border">
        <div className="container-page max-w-4xl">
          <span className="eyebrow">How Arif works</span>
          <h2 className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight leading-tight text-balance">
            A quick story before the LinkedIn follow.
          </h2>
          <div className="mt-8 grid md:grid-cols-12 gap-8 md:gap-10">
            <div className="md:col-span-7 space-y-5 text-base md:text-lg text-foreground/90 leading-relaxed">
              <p>
                Most technology problems on premium projects don't start with the technology. They start
                six months earlier — in a brief that didn't ask the right questions, a BOQ that copied
                the last one, and a coordination meeting that nobody documented.
              </p>
              <p>
                Arif's work begins where that drift begins. On a recent residence in Delhi NCR, the cinema
                was about to be built into a room that physically couldn't hold the speaker layout the
                owner was promised. A one-hour review, a redrawn shell, and the project went from a future
                disappointment to a reference room — without changing the equipment list.
              </p>
              <p>
                That's the pattern: <span className="font-medium text-foreground">engineering rigor + a strategist's view of risk</span>,
                applied early enough to matter. On LinkedIn, Arif shares the small, practical decisions
                behind these outcomes — the questions to ask architects, the mistakes to head off in
                design, and the field lessons from 1,000+ projects.
              </p>
            </div>
            <aside className="md:col-span-5">
              <div className="rounded-2xl border border-border bg-surface p-6 md:p-7">
                <span className="eyebrow text-accent">In one line</span>
                <p className="mt-3 text-lg font-medium leading-snug text-foreground">
                  "Arif brings the rare combination of deep engineering rigor and a strategist's view of risk…"
                </p>
                <div className="mt-5 flex flex-wrap gap-2 text-xs text-ink-soft">
                  <span className="rounded-full border border-border px-3 py-1">23+ years</span>
                  <span className="rounded-full border border-border px-3 py-1">1000+ projects</span>
                  <span className="rounded-full border border-border px-3 py-1">500km+ perimeter</span>
                </div>
                <a
                  href="#linkedin-cta"
                  onClick={() => trackEvent("linkedin_click", { source: "for-architects-microstory", variant: "see-feed" })}
                  className="mt-6 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-primary"
                >
                  See the LinkedIn feed <ArrowRight className="h-4 w-4" />
                </a>
                <a
                  href={SOCIAL.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow Arif Khan on LinkedIn"
                  onClick={() => trackEvent("linkedin_click", { source: "for-architects-microstory", variant: "follow-now" })}
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#0A66C2] text-white px-5 py-2.5 text-xs font-medium hover:opacity-90 transition-opacity"
                >
                  <Linkedin className="h-3.5 w-3.5" /> Follow now
                </a>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* ============= SECTION J: LINKEDIN CTA ============= */}
      <section id="linkedin-cta" className="py-20 md:py-28 border-b border-border bg-surface">
        <div className="container-page max-w-4xl">
          <div className="rounded-3xl border border-border bg-background p-8 md:p-12 flex flex-col md:flex-row gap-8 md:items-center">
            <div className="flex-none flex h-14 w-14 items-center justify-center rounded-2xl bg-[#0A66C2]/10 text-[#0A66C2]">
              <Linkedin className="h-7 w-7" strokeWidth={1.6} />
            </div>
            <div className="flex-1 min-w-0">
              <span className="eyebrow">On LinkedIn</span>
              <h2 className="mt-3 text-2xl md:text-3xl font-semibold tracking-tight">
                Continue the conversation on LinkedIn
              </h2>
              <p className="mt-4 text-sm md:text-base text-ink-soft leading-relaxed">
                Arif regularly shares practical notes for architects and project teams on smart homes,
                security planning, AV coordination, home cinema design, auditorium technology, and
                technology mistakes to avoid during execution.
              </p>
              <div className="mt-6">
                <a
                  href={SOCIAL.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow Arif Khan on LinkedIn"
                  onClick={() => trackEvent("linkedin_click", { source: "for-architects-cta", variant: "primary" })}
                  className="inline-flex items-center gap-2 rounded-full bg-[#0A66C2] text-white px-6 py-3 text-sm font-medium hover:opacity-90 transition-opacity"
                >
                  <Linkedin className="h-4 w-4" /> Follow Arif on LinkedIn
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ConsultingLinks intro="Discipline-by-discipline guidance for design teams planning projects across India:" />

      {/* ============= SECTION 10: FINAL CTA ============= */}
      <section className="py-20 md:py-28 border-b border-border bg-primary text-primary-foreground">
        <div className="container-page max-w-4xl text-center">
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight leading-tight">
            Planning a project where technology must disappear into the design?
          </h2>
          <p className="mt-6 text-base md:text-lg text-primary-foreground/85 leading-relaxed max-w-2xl mx-auto">
            Share your drawings, project stage, and technology expectations. A timely review can help
            avoid rework, design compromise, and confused execution later.
          </p>
          <div className="mt-10 flex flex-wrap gap-3 justify-center">
            <a
              href={MAILTO}
              onClick={() => trackEvent("email_click", { source: "for-architects-final-cta" })}
              className="inline-flex items-center gap-2 rounded-full bg-background text-foreground px-6 py-3 text-sm font-medium hover:opacity-95"
            >
              <Mail className="h-4 w-4" /> Email Your Requirement
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-full border border-primary-foreground/30 px-6 py-3 text-sm font-medium hover:bg-primary-foreground/10"
            >
              Let's Talk <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <p className="mt-8 text-xs text-primary-foreground/70">{EMAIL}</p>
        </div>
      </section>

      <section className="py-20 border-b border-border">
        <div className="container-page max-w-4xl">
          <AuthorCard />
        </div>
      </section>
    </PageShell>
  );
}

/* ============= GRAPHICS ============= */

function GraphicCard({
  title,
  caption,
  children,
}: {
  title: string;
  caption: string;
  children: React.ReactNode;
}) {
  return (
    <Reveal>
      <figure className="rounded-2xl border border-border bg-background p-6 md:p-8 h-full">
        <div className="rounded-xl bg-surface-soft border border-border/70 p-4 md:p-6">
          {children}
        </div>
        <figcaption className="mt-5">
          <h3 className="text-sm font-semibold tracking-tight">{title}</h3>
          <p className="mt-1.5 text-[13px] text-ink-soft leading-relaxed">{caption}</p>
        </figcaption>
      </figure>
    </Reveal>
  );
}

const STROKE = "currentColor";

function BuildingLayersDiagram() {
  return (
    <div className="rounded-2xl border border-border bg-surface-soft p-6 md:p-10">
      <svg viewBox="0 0 520 340" role="img" aria-label="Building technology layers for security, automation, AV, network and experience" className="w-full h-auto text-foreground/70">
        {/* ground */}
        <line x1="20" y1="305" x2="500" y2="305" stroke={STROKE} strokeWidth="0.8" />
        {/* building outline */}
        <polygon points="120,305 120,140 260,80 400,140 400,305" fill="none" stroke={STROKE} strokeWidth="1.1" />
        {/* floors */}
        <line x1="120" y1="200" x2="400" y2="200" stroke={STROKE} strokeWidth="0.6" strokeDasharray="3 3" />
        <line x1="120" y1="250" x2="400" y2="250" stroke={STROKE} strokeWidth="0.6" strokeDasharray="3 3" />
        {/* roofline accent */}
        <line x1="120" y1="140" x2="400" y2="140" stroke={STROKE} strokeWidth="0.6" strokeDasharray="3 3" />

        {/* CCTV */}
        <g stroke={STROKE} strokeWidth="1" fill="none">
          <circle cx="150" cy="165" r="4" />
          <circle cx="370" cy="165" r="4" />
          <circle cx="260" cy="115" r="4" />
        </g>
        {/* Wi-Fi rings */}
        <g stroke="hsl(145 50% 45%)" strokeWidth="0.9" fill="none" opacity="0.7">
          <circle cx="200" cy="225" r="22" />
          <circle cx="200" cy="225" r="36" />
          <circle cx="320" cy="275" r="22" />
          <circle cx="320" cy="275" r="36" />
        </g>
        {/* speakers */}
        <g fill={STROKE} opacity="0.7">
          <rect x="170" y="222" width="5" height="6" rx="1" />
          <rect x="345" y="272" width="5" height="6" rx="1" />
          <rect x="245" y="222" width="5" height="6" rx="1" />
        </g>
        {/* cable pathways */}
        <path d="M260,140 L260,200 L160,200 L160,250 L260,250 L260,290 L360,290" stroke={STROKE} strokeWidth="0.8" fill="none" strokeDasharray="2 4" />

        {/* layer labels */}
        <g fontFamily="ui-sans-serif, system-ui" fontSize="9" fill="currentColor" opacity="0.65">
          <text x="412" y="145">Security</text>
          <text x="412" y="205">Automation</text>
          <text x="412" y="255">AV</text>
          <text x="412" y="293">Network</text>
          <text x="412" y="318">Experience</text>
        </g>
      </svg>
    </div>
  );
}

function InfrastructureMap() {
  return (
    <svg viewBox="0 0 480 240" role="img" aria-label="Hidden infrastructure map of cabling, rack, Wi-Fi, CCTV, AV and automation" className="w-full h-auto text-foreground/70">
      <rect x="20" y="20" width="440" height="200" rx="4" fill="none" stroke={STROKE} strokeWidth="1" />
      {/* internal walls */}
      <line x1="180" y1="20" x2="180" y2="220" stroke={STROKE} strokeWidth="0.8" />
      <line x1="180" y1="120" x2="320" y2="120" stroke={STROKE} strokeWidth="0.8" />
      <line x1="320" y1="20" x2="320" y2="220" stroke={STROKE} strokeWidth="0.8" />
      {/* rack */}
      <rect x="30" y="180" width="28" height="30" fill="none" stroke={STROKE} />
      <text x="32" y="175" fontSize="8" fill="currentColor" opacity="0.6">RACK</text>
      {/* cable pathways */}
      <g stroke="hsl(220 30% 40%)" strokeWidth="0.9" fill="none" strokeDasharray="3 3">
        <path d="M58,195 L150,195 L150,60 L250,60 L250,120" />
        <path d="M150,195 L300,195 L300,160 L420,160" />
        <path d="M250,60 L400,60" />
      </g>
      {/* APs */}
      <g stroke="hsl(145 50% 45%)" strokeWidth="0.9" fill="none">
        <circle cx="120" cy="100" r="14" />
        <circle cx="120" cy="100" r="22" />
        <circle cx="380" cy="170" r="14" />
        <circle cx="380" cy="170" r="22" />
      </g>
      {/* cameras */}
      <g fill={STROKE}>
        <circle cx="40" cy="35" r="3" />
        <circle cx="455" cy="35" r="3" />
        <circle cx="455" cy="205" r="3" />
      </g>
      {/* speakers */}
      <g stroke={STROKE} fill="none">
        <rect x="240" y="155" width="6" height="8" />
        <rect x="280" y="155" width="6" height="8" />
        <rect x="350" y="35" width="6" height="8" />
      </g>
    </svg>
  );
}

function CinemaPlan() {
  return (
    <svg viewBox="0 0 480 240" role="img" aria-label="Home cinema room planning with screen, seating distance and speaker zones" className="w-full h-auto text-foreground/70">
      <rect x="40" y="20" width="400" height="200" rx="4" fill="none" stroke={STROKE} strokeWidth="1" />
      {/* screen */}
      <rect x="70" y="40" width="340" height="14" fill="currentColor" opacity="0.18" stroke={STROKE} />
      <text x="70" y="35" fontSize="8" fill="currentColor" opacity="0.65">SCREEN</text>
      {/* speakers */}
      <g fill={STROKE}>
        <rect x="70" y="65" width="8" height="10" />
        <rect x="402" y="65" width="8" height="10" />
        <rect x="232" y="63" width="14" height="6" />
        <rect x="60" y="120" width="6" height="10" />
        <rect x="414" y="120" width="6" height="10" />
        <rect x="60" y="175" width="6" height="10" />
        <rect x="414" y="175" width="6" height="10" />
      </g>
      {/* seating rows */}
      <g stroke={STROKE} fill="none">
        {[140, 170, 200].map((y) => (
          <g key={y}>
            {[150, 200, 250, 300].map((x) => (
              <rect key={x} x={x} y={y} width="36" height="14" rx="2" />
            ))}
          </g>
        ))}
      </g>
      {/* seating distance line */}
      <line x1="240" y1="54" x2="240" y2="140" stroke="hsl(145 50% 45%)" strokeWidth="0.9" strokeDasharray="3 3" />
      <text x="246" y="100" fontSize="8" fill="hsl(145 40% 35%)">seating distance</text>
      {/* acoustic side hatch */}
      <g stroke={STROKE} strokeWidth="0.4" opacity="0.5">
        {Array.from({ length: 12 }).map((_, i) => (
          <line key={`l${i}`} x1={45} y1={60 + i * 14} x2={55} y2={60 + i * 14} />
        ))}
        {Array.from({ length: 12 }).map((_, i) => (
          <line key={`r${i}`} x1={425} y1={60 + i * 14} x2={435} y2={60 + i * 14} />
        ))}
      </g>
    </svg>
  );
}

function EntrancePlan() {
  return (
    <svg viewBox="0 0 480 240" role="img" aria-label="Entrance experience plan with gate automation, boom barrier, video door phone and CCTV" className="w-full h-auto text-foreground/70">
      {/* road */}
      <rect x="20" y="100" width="440" height="60" fill="none" stroke={STROKE} strokeWidth="0.6" strokeDasharray="2 4" />
      <line x1="20" y1="130" x2="460" y2="130" stroke={STROKE} strokeWidth="0.4" strokeDasharray="4 6" opacity="0.6" />
      {/* gate posts */}
      <rect x="118" y="60" width="6" height="40" fill="currentColor" opacity="0.7" />
      <rect x="118" y="160" width="6" height="40" fill="currentColor" opacity="0.7" />
      <text x="100" y="50" fontSize="8" fill="currentColor" opacity="0.65">GATE</text>
      {/* gate swing */}
      <path d="M124,80 A 70 70 0 0 1 190 100" fill="none" stroke={STROKE} strokeDasharray="2 3" />
      <path d="M124,180 A 70 70 0 0 0 190 160" fill="none" stroke={STROKE} strokeDasharray="2 3" />
      {/* boom barrier */}
      <rect x="248" y="124" width="4" height="14" fill="currentColor" opacity="0.7" />
      <line x1="252" y1="130" x2="330" y2="100" stroke={STROKE} strokeWidth="1.4" />
      <text x="240" y="160" fontSize="8" fill="currentColor" opacity="0.65">BOOM</text>
      {/* VDP */}
      <rect x="140" y="42" width="10" height="12" fill="none" stroke={STROKE} />
      <text x="155" y="50" fontSize="8" fill="currentColor" opacity="0.65">VDP</text>
      {/* camera coverage */}
      <g stroke={STROKE} strokeWidth="0.9" fill="none">
        <circle cx="380" cy="60" r="3" />
        <path d="M380,60 L300,140 L460,140 Z" stroke="hsl(220 30% 40%)" strokeDasharray="2 3" opacity="0.6" />
      </g>
      <text x="370" y="50" fontSize="8" fill="currentColor" opacity="0.65">CCTV</text>
      {/* access reader */}
      <circle cx="100" cy="130" r="4" fill="hsl(145 50% 45%)" />
      <text x="55" y="118" fontSize="8" fill="currentColor" opacity="0.65">ACCESS</text>
    </svg>
  );
}

function TimelineGraphic() {
  const items = ["Concept", "Design", "Coordination", "BOQ", "Execution", "Handover"];
  return (
    <svg viewBox="0 0 480 140" role="img" aria-label="Technology planning timeline: concept, design, coordination, BOQ, execution, handover" className="w-full h-auto text-foreground/70">
      <line x1="30" y1="70" x2="450" y2="70" stroke={STROKE} strokeWidth="0.8" />
      {items.map((label, i) => {
        const x = 30 + (i * 420) / (items.length - 1);
        const isPlan = i < 2;
        return (
          <g key={label}>
            <circle
              cx={x}
              cy={70}
              r={isPlan ? 7 : 4.5}
              fill={isPlan ? "hsl(145 50% 45%)" : "white"}
              stroke={STROKE}
              strokeWidth="1"
            />
            <text x={x} y={102} fontSize="9" textAnchor="middle" fill="currentColor" opacity="0.8">
              {label}
            </text>
            {isPlan && (
              <text x={x} y={48} fontSize="8" textAnchor="middle" fill="hsl(145 40% 35%)">
                plan tech
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}
